import React, { useState } from 'react';
import { Calculator } from 'lucide-react';
import { SEO } from '../components/SEO';

type CalculationType = 'percentage' | 'value' | 'total' | 'increase' | 'decrease' | 'difference';

interface CalculationState {
  value1: string;
  value2: string;
  result: number | null;
}

function PostotakCalculator() {
  const [calculationType, setCalculationType] = useState<CalculationType>('percentage');
  const [values, setValues] = useState<CalculationState>({
    value1: '',
    value2: '',
    result: null
  });

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Kalkulator Postotaka",
    "applicationCategory": "CalculatorApplication", 
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": "Besplatni kalkulator za izračun postotaka. Izračunajte postotak od broja, povećanje, smanjenje i razliku u postocima.",
    "url": "https://online-kalkulator.com/izracun-postotka",
    "inLanguage": "hr",
    "author": {
      "@type": "Organization",
      "name": "Online Kalkulator"
    },
    "featureList": [
      "Izračun postotka od broja",
      "Izračun povećanja i smanjenja",
      "Izračun postotne razlike",
      "Automatsko ažuriranje rezultata",
      "Besplatno korištenje"
    ]
  };

  const calculateResult = () => {
    const num1 = parseFloat(values.value1);
    const num2 = parseFloat(values.value2);

    if (isNaN(num1) || isNaN(num2)) {
      return null;
    }

    switch (calculationType) {
      case 'percentage':
        // Koliki je postotak X od Y
        return (num1 / num2) * 100;
      case 'value':
        // Koliko je X% od Y
        return (num1 / 100) * num2;
      case 'total':
        // Koji broj je Y ako je X njegov Z%
        return (num2 / num1) * 100;
      case 'increase':
        // Povećanje za X%
        return num1 * (1 + (num2 / 100));
      case 'decrease':
        // Smanjenje za X%
        return num1 * (1 - (num2 / 100));
      case 'difference':
        // Postotna razlika između X i Y
        return ((num2 - num1) / num1) * 100;
      default:
        return null;
    }
  };

  const getInputLabels = () => {
    switch (calculationType) {
      case 'percentage':
        return { value1: 'Broj', value2: 'Od ukupno' };
      case 'value':
        return { value1: 'Postotak (%)', value2: 'Od broja' };
      case 'total':
        return { value1: 'Postotak (%)', value2: 'Vrijednost' };
      case 'increase':
        return { value1: 'Početni iznos', value2: 'Postotak povećanja (%)' };
      case 'decrease':
        return { value1: 'Početni iznos', value2: 'Postotak smanjenja (%)' };
      case 'difference':
        return { value1: 'Početna vrijednost', value2: 'Završna vrijednost' };
      default:
        return { value1: 'Vrijednost 1', value2: 'Vrijednost 2' };
    }
  };

  const getResultLabel = () => {
    switch (calculationType) {
      case 'percentage':
        return 'Postotak:';
      case 'value':
        return 'Iznos:';
      case 'total':
        return 'Ukupno:';
      case 'increase':
        return 'Nakon povećanja:';
      case 'decrease':
        return 'Nakon smanjenja:';
      case 'difference':
        return 'Postotna razlika:';
      default:
        return 'Rezultat:';
    }
  };

  const handleInputChange = (field: keyof CalculationState, value: string) => {
    setValues(prev => ({
      ...prev,
      [field]: value,
      result: null
    }));
  };

  const handleCalculate = () => {
    const result = calculateResult();
    setValues(prev => ({
      ...prev,
      result: result !== null ? parseFloat(result.toFixed(2)) : null
    }));
  };

  return (
    <>
      <SEO 
        title="Kalkulator Postotaka | Izračun Postotka Online"
        description="Jednostavan kalkulator za izračun postotaka. Izračunajte postotak od broja, povećanje, smanjenje i razliku u postocima brzo i precizno."
        canonicalUrl="/izracun-postotka"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <Calculator className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-800 text-center">Izračun Postotka</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Calculator Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="space-y-6">
              {/* Calculation Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Odaberite vrstu izračuna:
                </label>
                <select
                  value={calculationType}
                  onChange={(e) => setCalculationType(e.target.value as CalculationType)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="percentage">Koliki je postotak X od Y</option>
                  <option value="value">Koliko je X% od Y</option>
                  <option value="total">Koji broj je Y ako je X njegov Z%</option>
                  <option value="increase">Povećanje za postotak</option>
                  <option value="decrease">Smanjenje za postotak</option>
                  <option value="difference">Postotna razlika između brojeva</option>
                </select>
              </div>

              {/* Input Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {getInputLabels().value1}
                  </label>
                  <input
                    type="number"
                    value={values.value1}
                    onChange={(e) => handleInputChange('value1', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Unesite broj"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {getInputLabels().value2}
                  </label>
                  <input
                    type="number"
                    value={values.value2}
                    onChange={(e) => handleInputChange('value2', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Unesite broj"
                  />
                </div>
              </div>

              {/* Calculate Button */}
              <button
                onClick={handleCalculate}
                className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                Izračunaj
              </button>

              {/* Result */}
              {values.result !== null && (
                <div className="border-t pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">{getResultLabel()}</span>
                    <span className="text-xl font-bold text-blue-600">
                      {values.result}
                      {calculationType === 'percentage' || 
                       calculationType === 'difference' ? '%' : ''}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Information Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">O Kalkulatoru Postotaka</h2>
                <p className="text-gray-600 leading-relaxed">
                  Kalkulator postotaka je svestran alat koji vam omogućuje različite vrste izračuna s postocima.
                  Bilo da trebate izračunati postotak od broja, povećanje ili smanjenje za određeni postotak,
                  ili postotnu razliku između dva broja, ovaj kalkulator će vam pomoći da brzo dođete do rezultata.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Dostupni Izračuni</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Izračun postotka jednog broja od drugog</li>
                  <li>Izračun vrijednosti određenog postotka od broja</li>
                  <li>Pronalaženje ukupnog broja na temelju postotka</li>
                  <li>Izračun povećanja za određeni postotak</li>
                  <li>Izračun smanjenja za određeni postotak</li>
                  <li>Izračun postotne razlike između dva broja</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Koristiti</h2>
                <ol className="list-decimal list-inside text-gray-600 space-y-2">
                  <li>Odaberite vrstu izračuna iz padajućeg izbornika</li>
                  <li>Unesite potrebne vrijednosti u polja za unos</li>
                  <li>Kliknite "Izračunaj" za dobivanje rezultata</li>
                  <li>Rezultat će biti prikazan ispod s odgovarajućom oznakom</li>
                </ol>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Napomene</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Svi rezultati su zaokruženi na dvije decimale</li>
                  <li>Kalkulator automatski provjerava ispravnost unesenih brojeva</li>
                  <li>Za decimalne brojeve koristite točku kao decimalni separator</li>
                  <li>Negativni brojevi su podržani za odgovarajuće izračune</li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default PostotakCalculator;